import React, { useState, useEffect } from 'react';
import LogoImage from './../../main/logos/ffailogo1.png';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Tabs,
  Tab,
  Tooltip,
  TabList,
  TabPanels,
  TabPanel,
  Grid,
  Column,
  Link,
} from '@carbon/react';
import { InfoSection, InfoCard } from '../../components/Info';
import {
  Demo,
  ForecastLightning,
  Application,
  GraphicalDataFlow,
} from '@carbon/react/icons';
import { v4 as uuidv4 } from 'uuid';
import ConversationHistory from './ConversationHistory';
import Agents from './Agents';
import Insights from './Insights';
import AIAssistant from './AIAssistant';
import UserFiles from './UserFiles';
import TrackedYouTube from './../../components/TrackedYoutube';
import amplitude from './../../analytics/amplitude';

// --------------------------------------------------------------
// 🔗 One-minute overview video URL
// --------------------------------------------------------------
const OVERVIEW_VIDEO_ID = 'zIgN4YUFtlk'; // YouTube video ID
const YT_EMBED_URL = `https://www.youtube.com/embed/${OVERVIEW_VIDEO_ID}`;

// --------------------------------------------------------------
// 🔗 Full 15-minute tutorial video URL
// --------------------------------------------------------------

const FULL_TUTORIAL_VIDEO_ID = 'fpDIEStxGaA'; // YouTube video ID
const FULL_YT_EMBED_URL = `https://www.youtube.com/embed/${FULL_TUTORIAL_VIDEO_ID}`;

const LandingPage = ({ token, user }) => {
  const navigate = useNavigate();

  // Initialize state from localStorage or default values
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('activeTab');
    return savedTab ? parseInt(savedTab, 10) : 0;
  });

  const [conversationId, setConversationId] = useState(() => {
    return localStorage.getItem('conversationId') || uuidv4();
  });

  const [conversationHistory, setConversationHistory] = useState(() => {
    const savedHistory = localStorage.getItem('conversationHistory');
    return savedHistory ? JSON.parse(savedHistory) : [];
  });

  const [input, setInput] = useState(() => {
    return localStorage.getItem('input') || '';
  });

  const [response, setResponse] = useState(() => {
    return localStorage.getItem('response') || '';
  });

  const [placeholderText, setPlaceholderText] = useState(() => {
    return (
      localStorage.getItem('placeholderText') ||
      "Chat with Fresh Focus AI - What's on your mind?"
    );
  });

  // Save activeTab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  // Save conversationId to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('conversationId', conversationId);
  }, [conversationId]);

  // Save conversationHistory to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(
      'conversationHistory',
      JSON.stringify(conversationHistory)
    );
  }, [conversationHistory]);

  // Save input to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('input', input);
  }, [input]);

  // Save response to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('response', response);
  }, [response]);

  // Save placeholderText to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('placeholderText', placeholderText);
  }, [placeholderText]);

  const handleTabChange = ({ selectedIndex }) => {
    console.info(`Setting active tab to ${selectedIndex}`);
    setActiveTab(selectedIndex);
  };

  const historyTabFocus = () => {
    console.info('History tab selected');
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleCreateAccountClick = () => {
    navigate('/createaccount');
  };

  const onRestoreConversation = (conversationId, history) => {
    setConversationHistory(history);
    setConversationId(conversationId);
    setResponse('');
    setInput('');

    if (history && history.length > 0 && history[0].question) {
      const truncatedQuestion =
        history[0].question.length > 100
          ? history[0].question.substring(0, 100) + '...'
          : history[0].question;

      setPlaceholderText(`Continuing conversation: ${truncatedQuestion}`);
    } else {
      setPlaceholderText("Chat with Fresh Focus AI - What's on your mind?");
    }

    setActiveTab(0);
  };

  // Clear stored data when user logs out or token becomes invalid
  useEffect(() => {
    if (!token) {
      // Clear all stored data related to this component
      localStorage.removeItem('activeTab');
      localStorage.removeItem('conversationId');
      localStorage.removeItem('conversationHistory');
      localStorage.removeItem('input');
      localStorage.removeItem('response');
      localStorage.removeItem('placeholderText');

      // Reset state
      setActiveTab(0);
      setConversationId(uuidv4());
      setConversationHistory([]);
      setInput('');
      setResponse('');
      setPlaceholderText("Chat with Fresh Focus AI - What's on your mind?");
    }
  }, [token]);

  // --------------------------------------------------------------
  // 🎥 Video embed helpers
  // --------------------------------------------------------------

  const VideoEmbed = () => (
    <div>
      <p>
        <strong>Quick Overview (1 Minute)</strong>
      </p>
      <p>
        This is a short, 1-minute video to give you a quick overview of Fresh
        Focus AI.
      </p>
      <TrackedYouTube
        videoId={OVERVIEW_VIDEO_ID}
        title="Fresh Focus AI – 1-Minute Overview"
        eventBase="Landing Overview"
      />
    </div>
  );

  const FullVideoEmbed = () => (
    <div>
      <p>
        If you'd like a more in-depth look at Fresh Focus AI, watch the full
        15-minute tutorial below.
      </p>
      <TrackedYouTube
        videoId={FULL_TUTORIAL_VIDEO_ID}
        title="Fresh Focus AI – 15-Minute Tutorial"
        eventBase="Landing Full Tutorial"
      />
    </div>
  );

  const MCP_VIDEO_ID = 'g50i2rDlZks';
  const MCP_VIDEO_URL = `https://youtu.be/${MCP_VIDEO_ID}`;

  function handleMcpVideoClick() {
    amplitude.track('Watch MCP Tutorial', {
      videoId: MCP_VIDEO_ID,
      page: 'LandingPage',
      tab: 'About',
    });

    window.open(MCP_VIDEO_URL, '_blank', 'noopener');
  }

  return (
    <Grid className="landing-page" fullWidth>
      <Column lg={16} md={8} sm={4} className="landing-page__banner">
        {token ? (
          <div />
        ) : (
          <h1 className="landing-page__heading">Try out Fresh Focus AI</h1>
        )}
      </Column>
      <Column lg={16} md={8} sm={4} className="landing-page__r2">
        <Tabs selectedIndex={activeTab} onChange={handleTabChange}>
          <TabList className="tabs-group" aria-label="Tab navigation">
            <Tab>{token ? 'AI' : 'About'}</Tab>
            <Tab handleTabAnchorFocus={historyTabFocus}>History</Tab>
            <Tab>Insights</Tab>
            <Tab>Agents</Tab>
            <Tab>Files</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              {token ? (
                <Grid className="tabs-group-content">
                  <Column
                    md={8}
                    lg={16}
                    sm={4}
                    className="landing-page__tab-content">
                    <AIAssistant
                      token={token}
                      user={user}
                      placeholderText={placeholderText}
                      propInput={input}
                      setInput={setInput}
                      propConversationId={conversationId}
                      setConversationId={setConversationId}
                      propConversationHistory={conversationHistory}
                      setConversationHistory={setConversationHistory}
                      propResponse={response}
                      setResponse={setResponse}
                    />
                  </Column>
                </Grid>
              ) : (
                <Grid className="tabs-group-content">
                  <Column
                    md={4}
                    lg={7}
                    sm={4}
                    className="landing-page__tab-content">
                    <h4>
                      AI that works for you, even while you sleep.
                    </h4>
                    <div className="landing-page__p">
                      <ul
                        className="space-y-1.5 mb-3"
                        style={{
                          listStyleType: 'disc',
                          paddingLeft: '1rem',
                          fontSize: '0.8em',
                        }}>
                        <li>
                          <strong>Schedule AI agents</strong> that run
                          daily, weekly, or on any schedule. Just describe
                          what you need in plain English. No flowcharts or
                          programming required.
                        </li>
                        <li>
                          <strong>Search 15+ sources at once:</strong>{' '}
                          Google, news, Reddit, PubMed, YouTube, finance
                          data, and more, all in a single question with
                          cited results.
                        </li>
                        <li>
                          <strong>40+ AI models, one place:</strong>{' '}
                          ChatGPT, Claude, Gemini, Grok, DeepSeek &
                          Cohere. Compare side-by-side. $15/month,
                          ad-free.
                        </li>
                        <li>
                          <strong>Integrations that act:</strong> save to
                          Google Drive, Evernote, post to GitHub, send
                          email & SMS, connect to thousands of apps via
                          MCP tools.
                          &nbsp;&nbsp;&nbsp;
                          <Tooltip
                            label="Watch a video that shows you exactly how to connect powerful third-party tools in minutes using MCP"
                            align="bottom">
                            <Button
                              kind="ghost"
                              size="sm"
                              onClick={handleMcpVideoClick}
                              className="tooltip-button"
                              aria-label="Watch MCP tutorial video">
                              <Demo />
                            </Button>
                          </Tooltip>
                        </li>
                        <li>
                          <strong>Analyze data on the spot:</strong> upload
                          a spreadsheet, financial data, or logs and the
                          AI generates and runs code in a secure sandbox
                          to crunch the numbers for you.
                        </li>
                        <li>
                          <strong>Daily personalized insights</strong>{' '}
                          generated automatically from your conversations
                          and interests.
                        </li>
                      </ul>
                    </div>

                    <Button className="login-button" onClick={handleLoginClick}>
                      Log in
                    </Button>

                    {/* 🎬 Responsive YouTube embed */}
                    <VideoEmbed />

                    {/* 📖 Optional full tutorial (kept secondary) */}
                    <details style={{ marginBottom: '1rem' }}>
                      <summary
                        style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                        Watch the full 15-minute tutorial
                      </summary>
                      <FullVideoEmbed />
                    </details>

                    <p>
                      Welcome early users!{' '}
                      <Link href="/#/login" onClick={handleLoginClick}>
                        Login
                      </Link>{' '}
                      or{' '}
                      <Link
                        href="/#/createaccount"
                        onClick={handleCreateAccountClick}>
                        create an account
                      </Link>{' '}
                      to get started. Enjoy 1 week free trial (credit card
                      required).
                    </p>
                  </Column>
                  <Column md={4} lg={{ span: 8, offset: 7 }} sm={4}>
                    <img
                      className="landing-page__illo"
                      src={LogoImage}
                      alt="DLT Logo"
                    />
                  </Column>
                </Grid>
              )}
            </TabPanel>
            <TabPanel>
              <Grid className="tabs-group-content">
                <Column
                  lg={16}
                  md={8}
                  sm={4}
                  className="landing-page__tab-content">
                  <div className="accordion-top-space">
                    Here are your previous conversations. You can view them,
                    restore & continue them, or delete them here.
                    {/* NEW: quick video-help link */}
                    <button
                      onClick={() =>
                        window.open(
                          'https://youtu.be/bxmLrQOhI28',
                          '_blank',
                          'noopener'
                        )
                      }
                      title="Watch a 1-minute tutorial about this History page"
                      style={{
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                      }}>
                      <Demo size={18} />
                    </button>
                  </div>
                  <div className="accordion-top-space">
                    <ConversationHistory
                      token={token}
                      isActive={activeTab === 1}
                      onRestoreConversation={onRestoreConversation}
                    />
                  </div>
                </Column>
              </Grid>
            </TabPanel>
            <TabPanel>
              <Grid className="tabs-group-content">
                <Column
                  lg={16}
                  md={8}
                  sm={4}
                  className="landing-page__tab-content">
                  <Insights
                    token={token}
                    isActive={activeTab === 2}
                    user={user}
                  />
                </Column>
              </Grid>
            </TabPanel>
            <TabPanel>
              <Grid className="tabs-group-content">
                <Column
                  lg={16}
                  md={8}
                  sm={4}
                  className="landing-page__tab-content">
                  <Agents
                    token={token}
                    isActive={activeTab === 3}
                    user={user}
                  />
                </Column>
              </Grid>
            </TabPanel>
            <TabPanel>
              <Grid className="tabs-group-content">
                <Column
                  lg={16}
                  md={8}
                  sm={4}
                  className="landing-page__tab-content">
                  <UserFiles
                    token={token}
                    isActive={activeTab === 4}
                    user={user}
                  />
                </Column>
              </Grid>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Column>
      {token ? (
        <div />
      ) : (
        <Column lg={16} md={8} sm={4} className="landing-page__r3">
          <InfoSection heading="The Principles">
            <InfoCard
              heading="Multi-Model Intelligence"
              body="Engage with leading AI models including ChatGPT, Claude, Gemini, DeepSeek, Cohere, and Grok in a single interface. Compare responses across different models or switch mid-conversation to leverage unique strengths for each query."
              icon={() => <GraphicalDataFlow size={32} />}
            />
            <InfoCard
              heading="Advanced Integration & Tools"
              body="Harness powerful integrations with over 15 search engines, cloud storage solutions (Google Drive, S3, Evernote, Github), and thousands of MCP tools through Zapier. Fresh Focus AI streamlines your workflow, saving and analyzing data precisely where you need it."
              icon={() => <Application size={32} />}
            />
            <InfoCard
              heading="Smart Automation & Insight"
              body="Automate complex, recurring tasks effortlessly, run Python and Node.js code in a secure sandbox, and receive customized daily insights tailored to your interests. Fresh Focus AI proactively delivers essential information directly through email and SMS text notifications, or directly to storage of your choice."
              icon={() => <ForecastLightning size={32} />}
            />
          </InfoSection>
          <a
            href="/privacy-policy.html"
            target="_blank"
            rel="noopener noreferrer">
            Privacy Policy
          </a>
        </Column>
      )}
    </Grid>
  );
};

export default LandingPage;
